import { useState, type ReactNode } from "react";
import { PILOT, STORAGE_KEYS } from "../../config";
import { BrandGlyph } from "./BrandGlyph";

/**
 * Closed-beta gate: a lightweight passcode lock, then a one-time
 * intended-use / consent acknowledgement. Both persist in localStorage.
 */
export function PilotGate({ children }: { children: ReactNode }) {
  const [unlocked, setUnlocked] = useState(
    () => PILOT.gateDisabled || safeGet(STORAGE_KEYS.unlock) === "1",
  );
  const [consented, setConsented] = useState(
    () => safeGet(STORAGE_KEYS.consent) === "1",
  );

  if (!unlocked) return <Passcode onOk={() => { safeSet(STORAGE_KEYS.unlock, "1"); setUnlocked(true); }} />;
  if (!consented) return <Consent onAccept={() => { safeSet(STORAGE_KEYS.consent, "1"); setConsented(true); }} />;
  return <>{children}</>;
}

function Passcode({ onOk }: { onOk: () => void }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);
  const submit = () => {
    if (value.trim() === PILOT.passcode) onOk();
    else setError(true);
  };
  return (
    <div className="card hero" style={{ marginTop: 40 }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
        <BrandGlyph size={40} />
      </div>
      <h2>Closed beta</h2>
      <p>Enter the access code to continue.</p>
      <input
        type="text"
        inputMode="text"
        autoCapitalize="off"
        autoCorrect="off"
        placeholder="Access code"
        value={value}
        onChange={(e) => { setValue(e.target.value); setError(false); }}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        style={{ marginTop: 16, textAlign: "center" }}
      />
      {error && <p style={{ color: "var(--danger)", marginTop: 8 }}>Wrong code.</p>}
      <button className="btn" style={{ marginTop: 14 }} onClick={submit}>
        Unlock
      </button>
    </div>
  );
}

function Consent({ onAccept }: { onAccept: () => void }) {
  const [checked, setChecked] = useState(false);
  return (
    <div className="card" style={{ marginTop: 28 }}>
      <div className="row" style={{ gap: 10 }}>
        <BrandGlyph size={30} />
        <strong style={{ textTransform: "none", fontSize: 17, color: "var(--text)" }}>
          Before you start
        </strong>
      </div>
      <div style={{ marginTop: 14, fontSize: 14, color: "var(--dim)", lineHeight: 1.6 }}>
        <p>
          <b style={{ color: "var(--text)" }}>Intended use.</b> CADtomie Gait is a
          functional gait <b>screening and progress-tracking aid</b> for
          clinicians. It is <b>not</b> a motion-lab replacement and <b>not</b> a
          diagnostic device. It does not provide validated clinical measurements;
          sagittal angles are estimated for movement-range context only.
        </p>
        <p>
          <b style={{ color: "var(--text)" }}>Evaluation status.</b> This is a
          closed beta. Scoring thresholds and reference ranges are seed values
          that are not yet clinically calibrated — interpret results as
          indicative only and verify against established methods.
        </p>
        <p>
          <b style={{ color: "var(--text)" }}>Privacy.</b> All video and analysis
          stay on this device in your browser. Nothing is uploaded. Obtain the
          patient's consent before recording, and delete sessions you no longer
          need.
        </p>
      </div>
      <label className="row" style={{ gap: 10, marginTop: 12, cursor: "pointer", color: "var(--text)" }}>
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        I understand and accept the above.
      </label>
      <button className="btn" style={{ marginTop: 16 }} disabled={!checked} onClick={onAccept}>
        Continue
      </button>
    </div>
  );
}

function safeGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function safeSet(k: string, v: string): void {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* ignore */
  }
}
